




















































































import type { PhoneNumber, CountryCode } from 'libphonenumber-js';
import parsePhoneNumberFromString from 'libphonenumber-js';
import { Vue, Component, Prop, Watch, Ref } from 'vue-property-decorator';
import debounce from 'debounce';
import { countryCodes } from '@getaccept/lib-shared/src/countries/constants/country-codes';
import type { CountryCodeObj } from '@getaccept/lib-shared/src/countries/constants/country-code';
import { testMobileNumber } from '../helpers';

@Component
export default class PhoneNumberInput extends Vue {
  @Prop() defaultCountryCode: string;
  @Prop() label?: string;
  @Prop({ default: '' }) value: string;
  @Prop() helpMessage: string;
  @Prop() errorMessage: string;
  @Prop({ default: false }) success: boolean;
  @Prop({ default: false }) disabled: boolean;
  @Prop() autocomplete: string;
  @Ref('countrySearch') countrySearchRef: HTMLInputElement;
  @Ref('countryButton') countryButtonRef: HTMLButtonElement;

  selectedCountryCode = '';
  open = false;
  number = '';
  search = '';
  fallbackCountryCode = 'SE';
  dropdownFocus = 0;

  @Watch('value', { immediate: true })
  watchValue(newValue: string) {
    this.setPhoneNumberValues(newValue);
    if (!!newValue && newValue.charAt(0) !== '+') {
      this.$emit('update', this.fullNumber);
    }
  }

  @Watch('search')
  watchSearch() {
    this.dropdownFocus = 0;
  }

  @Watch('internalError', { immediate: true })
  watchInternalError() {
    this.$emit('error', this.internalError);
  }

  get fullNumber(): string {
    const numberObject: PhoneNumber = parsePhoneNumberFromString(
      this.number,
      this.selectedCountryCodeObject.key as CountryCode
    );
    if (numberObject) {
      return numberObject.number.toString();
    }
    return this.number;
  }

  get selectedCountryCodeObject(): CountryCodeObj {
    if (this.selectedCountryCode) {
      return this.countryCodesList.find(
        countryCodeObject => countryCodeObject.key === this.selectedCountryCode
      );
    }
    if (this.defaultCountryCode) {
      return this.countryCodesList.find(
        countryCodeObject => countryCodeObject.key === this.defaultCountryCode
      );
    }
    return this.countryCodesList.find(
      countryCodeObject => countryCodeObject.key === this.fallbackCountryCode
    );
  }

  handleInput: (value: any) => void = debounce(value => this.handleUpdateNumber(value), 300);

  setPhoneNumberValues(phoneNumber: string) {
    const numberObject: PhoneNumber = parsePhoneNumberFromString(
      phoneNumber,
      this.selectedCountryCodeObject.key as CountryCode
    );
    if (numberObject) {
      this.number = numberObject.nationalNumber.toString();
      if (numberObject.country) {
        this.selectedCountryCode = numberObject.country;
      }
    } else {
      this.number = phoneNumber;
    }
  }

  clickOutside(event: any) {
    const countrySearchFieldClicked: boolean = event.target === this.countrySearchRef;
    if (!this.open || countrySearchFieldClicked) {
      return;
    }
    this.handleClose(false);
  }

  async handleToggle() {
    if (this.open) {
      this.handleClose(false);
      return;
    }
    this.open = true;
    await this.$nextTick();
    this.countrySearchRef.focus();
  }

  handleClose(keepFocus = true) {
    this.open = false;
    this.search = '';
    if (keepFocus) {
      this.countryButtonRef.focus();
    }
  }

  async handleChangeCountryCode(item: CountryCodeObj) {
    if (!item?.key) {
      return;
    }
    this.selectedCountryCode = item.key;
    this.handleClose();
    if (!this.number) {
      return;
    }
    this.$emit('update', this.fullNumber);
  }

  handleNavigate(event: KeyboardEvent) {
    event.preventDefault();

    const firstSelected: boolean = this.dropdownFocus === 0;
    const lastSelected: boolean = this.dropdownFocus === this.countryCodesList.length - 1;
    let step: number;
    switch (event.key) {
      case 'ArrowUp':
        if (firstSelected) {
          return;
        }
        step = -1;
        break;
      case 'ArrowDown':
        if (lastSelected) {
          return;
        }
        step = 1;
        break;
    }

    this.dropdownFocus += step;
  }

  handleUpdateNumber(event: any) {
    this.handleClose(false);
    const number = event.target.value;
    this.setPhoneNumberValues(number);
    this.$emit('update', this.fullNumber);
  }

  clearNumber() {
    this.number = '';
  }

  get internalError(): string {
    if (!this.number) {
      return '';
    }
    return testMobileNumber(this.fullNumber)
      ? ''
      : this.$t('enter-a-valid-phone-number').toString();
  }

  handleSelectCountry() {
    if (this.countryCodesList.length) {
      this.handleChangeCountryCode(this.filteredCountryCodesList[this.dropdownFocus]);
    }
  }

  get listeners(): Record<string, (arg?) => any> {
    return {
      ...this.$listeners,
      input: this.handleInput,
    };
  }

  get filteredCountryCodesList(): CountryCodeObj[] {
    return this.countryCodesList.filter(country => {
      if (!this.search) {
        return country;
      }
      return country.label.toLowerCase().includes(this.search.toLowerCase());
    });
  }
  get countryCodesList(): CountryCodeObj[] {
    return countryCodes.map((country: CountryCodeObj) => ({
      key: country.key,
      label: `${country.flag} (${country.code}) ${country.label}`,
      code: country.code,
      flag: country.flag,
    }));
  }

  getIndexOfValue(): number {
    return this.countryCodesList.findIndex(item => item.key === this.selectedCountryCode);
  }
}
